import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, Typography, Alert, Skeleton, Card, CardContent, alpha, useTheme } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import TemplateForm from '../components/TemplateForm';
import { templatesApi } from '../../../api/templates.api';
import useUIStore from '../../../store/useUIStore';

const TemplateCreatePage = ({ sess = { uid: '', role: 'user' } }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [loadingForm, setLoadingForm] = useState(true);
  const showSnackbar = useUIStore((state) => state.showSnackbar);
  const cardSx = {
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
    boxShadow: 'none',
  };
  const secondaryButtonSx = {
    textTransform: 'none',
    fontWeight: 600,
    borderColor: alpha(theme.palette.primary.main, 0.25),
    '&:hover': {
      borderColor: alpha(theme.palette.primary.main, 0.45),
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  };

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    return (
      data?.error?.error_user_msg ||
      data?.error?.message ||
      data?.message ||
      data?.error ||
      error?.message ||
      fallback
    );
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoadingForm(false), 180);
    return () => clearTimeout(timer);
  }, []);

  /**
   * Extract numeric variables from text (e.g., {{1}}, {{2}})
   * Returns sorted array of variable numbers as strings
   */
  const findNumericVariables = (text = '') => {
    const matches = text.match(/\{\{(\d+)\}\}/g) || [];
    const numbers = matches.map((m) => m.replace(/[{}]/g, '')).filter(Boolean);
    return Array.from(new Set(numbers)).sort((a, b) => Number(a) - Number(b));
  };

  /**
   * Build example array for variables
   * Meta API expects: body_text: [["sample1", "sample2"]] (nested array)
   */
  const buildExampleArray = (vars = [], samples = {}) => {
    if (!vars.length) return undefined;
    return vars.map((v) => samples?.[v] || `sample_${v}`);
  };

  const normalizeLanguageCode = (value) => {
    const raw = `${value || ''}`.trim();
    if (!raw) return 'en_US';
    if (/^en[-_]us$/i.test(raw)) return 'en_US';
    return raw;
  };

  /**
   * Build components array matching Meta WhatsApp Template API format
   * Reference: https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates
   */
  const buildComponents = (data) => {
    const components = [];
    const headerVars = findNumericVariables(data.header);
    const bodyVars = findNumericVariables(data.body);
    const headerSampleArray = buildExampleArray(headerVars, data.variableSamples?.header);
    const bodySampleArray = buildExampleArray(bodyVars, data.variableSamples?.body);

    // HEADER Component
    if (data.headerType && data.headerType !== 'none') {
      if (data.headerType === 'text' && data.header) {
        // Text header with optional variables
        const headerComponent = {
          type: 'HEADER',
          format: 'TEXT',
          text: data.header,
        };
        if (headerSampleArray && headerSampleArray.length > 0) {
          headerComponent.example = { header_text: headerSampleArray };
        }
        components.push(headerComponent);
      } else if (['image', 'video', 'document'].includes(data.headerType)) {
        // Media header (IMAGE, VIDEO, DOCUMENT)
        const headerComponent = {
          type: 'HEADER',
          format: data.headerType.toUpperCase(),
        };
        // header_handle is required for media headers
        if (data.headerHandle) {
          headerComponent.example = { header_handle: [data.headerHandle] };
        }
        components.push(headerComponent);
      }
    }

    // BODY Component (required)
    if (data.body) {
      const bodyComponent = {
        type: 'BODY',
        text: data.body,
      };
      // Meta API expects body_text as nested array: [["value1", "value2"]]
      if (bodySampleArray && bodySampleArray.length > 0) {
        bodyComponent.example = { body_text: [bodySampleArray] };
      }
      components.push(bodyComponent);
    }

    // FOOTER Component (optional)
    if (data.footer && data.footer.trim()) {
      components.push({
        type: 'FOOTER',
        text: data.footer.trim(),
      });
    }

    // BUTTONS Component (optional)
    if (Array.isArray(data.buttons) && data.buttons.length > 0) {
      const buttons = data.buttons
        .filter((btn) => btn && btn.text)
        .map((btn) => {
          if (btn.type === 'URL') {
            const urlButton = {
              type: 'URL',
              text: btn.text,
              url: btn.url, // Use the actual URL (static or dynamic with {{1}})
            };
            // For dynamic URLs, add example array
            // Dynamic URL example: url: "https://example.com/track?id={{1}}", example: ["ORD123456"]
            if (btn.urlType === 'DYNAMIC' && btn.urlVariable) {
              urlButton.example = [btn.urlVariable];
            }
            return urlButton;
          }
          if (btn.type === 'PHONE_NUMBER') {
            const phoneNumber = `${btn.phoneCountryCode || ''}${btn.phoneNumber || ''}`.replace(/[^+\d]/g, '');
            return {
              type: 'PHONE_NUMBER',
              text: btn.text,
              phone_number: phoneNumber,
            };
          }
          // QUICK_REPLY
          return {
            type: 'QUICK_REPLY',
            text: btn.text,
          };
        });

      if (buttons.length > 0) {
        components.push({
          type: 'BUTTONS',
          buttons,
        });
      }
    }

    return components;
  };

  const handleSubmit = async (data) => {
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Build components for Meta API
      const components = buildComponents(data);

      // Validate required fields
      if (!data.name || !data.name.trim()) {
        setStatus({ type: 'error', message: 'Template name is required' });
        setSubmitting(false);
        return;
      }

      if (!data.body || !data.body.trim()) {
        setStatus({ type: 'error', message: 'Template body is required' });
        setSubmitting(false);
        return;
      }

      // Check if media header requires a file or existing handle
      if (['image', 'video', 'document'].includes(data.headerType) && !data.headerFile && !data.headerHandle) {
        setStatus({ type: 'error', message: 'Please upload media for the header' });
        setSubmitting(false);
        return;
      }

      // Build payload matching Meta API format
      const payload = {
        name: data.name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        category: (data.category || 'MARKETING').toUpperCase(),
        language: normalizeLanguageCode(data.language),
        components,
      };

      // Add uid if available
      if (sess.uid) {
        payload.uid = sess.uid;
      }

      let response;
      if (data.headerFile) {
        const formData = new FormData();
        formData.append('media', data.headerFile);
        formData.append('name', payload.name);
        formData.append('category', payload.category);
        formData.append('language', payload.language);
        formData.append('components', JSON.stringify(payload.components));
        response = await templatesApi.create(formData);
      } else {
        response = await templatesApi.create(payload);
      }
      const responseData = response?.data || {};
      if (responseData?.success === false || responseData?.error) {
        const message = getApiErrorMessage({ response: { data: responseData } }, 'Failed to create template');
        setStatus({ type: 'error', message });
        showSnackbar({ message, severity: 'error' });
        return;
      }
      const created = response?.data?.data || response?.data || {};
      const message = response?.data?.message || 'Template created. Submit it from the templates list.';
      setStatus({ type: 'success', message });
      showSnackbar({ message, severity: 'success' });
      navigate('/templates', { replace: true });
      setTimeout(() => {
        window.location.assign('/templates');
      }, 50);
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to create template');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async (data) => {
    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const components = buildComponents(data);
      const payload = {
        name: data.name?.trim() || '',
        category: (data.category || 'MARKETING').toUpperCase(),
        language: normalizeLanguageCode(data.language),
        headerType: data.headerType || 'none',
        header: data.header || '',
        headerHandle: data.headerHandle || '',
        body: data.body || '',
        footer: data.footer || '',
        buttons: Array.isArray(data.buttons) ? data.buttons : [],
        variableSamples: data.variableSamples || {},
        components,
      };

      if (sess.uid) {
        payload.uid = sess.uid;
      }

      const response = await templatesApi.saveDraft(payload);
      const responseData = response?.data || {};
      if (responseData?.success === false || responseData?.error) {
        const message = getApiErrorMessage({ response: { data: responseData } }, 'Failed to save draft');
        setStatus({ type: 'error', message });
        showSnackbar({ message, severity: 'error' });
        return;
      }
      const message = response?.data?.message || 'Draft saved successfully';
      setStatus({ type: 'success', message });
      showSnackbar({ message, severity: 'success' });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to save draft');
      setStatus({ type: 'error', message });
      showSnackbar({ message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingForm) {
    return (
      <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={2}>
          <Skeleton variant="rounded" width={90} height={36} />
          <Skeleton variant="text" width={220} height={28} />
        </Stack>
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={160} />
          <Skeleton variant="rounded" height={260} />
          <Skeleton variant="rounded" height={160} />
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: alpha(theme.palette.primary.main, 0.02), minHeight: '100%' }}>
      <Card sx={{ ...cardSx, mb: 2, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)` }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Button variant="outlined" startIcon={<ArrowLeft size={16} />} href="/templates" sx={secondaryButtonSx}>
              Back
            </Button>
            <Box>
              <Typography variant="h5" fontWeight={700}>Create WhatsApp Template</Typography>
              <Typography variant="body2" color="text.secondary">
                Build a new template ready for review
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {status.message && status.type === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.message}
        </Alert>
      )}

      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <TemplateForm
          onSubmit={handleSubmit}
          onSaveDraft={handleSaveDraft}
          onCancel={() => (window.location.href = '/templates')}
          loading={submitting}
          isEdit={false}
        />
      </Box>
    </Box>
  );
};

export default TemplateCreatePage;
